import {
  handleSaveDraftActivity,
  handleCreateActivity,
  handleUpdateDraftActivity,
  handleUpdateActivity,
  validateActivityName,
  handleGetActivity
} from '@/services/activities';
import {useAppDispatch, useAppSelector} from '@/store';
import {
  resetActivityData,
  setActivityFormData
} from '@/store/slices/activitySlice';
import {setToastNotifications} from '@/store/slices/commonSlice';
import {
  ACTIVITY_TYPE,
  ACTIVITY_TYPES_ENUM,
  isVideoActivityType
} from '@/types/Activity';
import {
  getDateFromISOString,
  getUUID,
  formatUTCTimestamp,
  isPublished,
  handleNameValidationViaCallbackFn,
  isInProgress,
  getISOTimeStringForReview,
  getCurrencySymbol
} from '@/utils/commons';
import {ROUTES} from '@/utils/constants/routesConstants';
import {ACTIVITY_STATUS} from '@/utils/constants/activityConstants';
import {useCallback, useState} from 'react';
import {useSelector} from 'react-redux';
import {useNavigate} from 'react-router-dom';
import {COUNTRY_CODES_FREQUENCY} from '@/utils/constants/globalConstants';
import {useActivityValidation} from '@/hooks/useActivityValidation';
import {buildIncludedSegmentGroups} from '@/helpers/rewardsUtils';
import {mapIncludedSegmentGroups} from '@/utils/rewardPayloadUtils';
import {
  normalizeActivitySegmentInput,
  omitDeprecatedSegmentWireKeys,
  resolveDisplayOrderFromApi,
  resolveRawSegmentExcludeFromForm,
  resolveSegmentExcludeFromApi
} from '@/utils/activityReviewUtils';

const typeOfActivity = ['Welcome Activity', 'Login'];

/**
 * Activity segment fields — form vs API
 *
 * Redux / setup UI: `selectedSegments` (includes), `segmentExclude` (toggle), `excludedSegments` (chips)
 * — same field names as Rewards SegmentsCard expects. Requests must not send deprecated wire keys
 * `segments` or `excludeSegments`; use `includedSegmentGroups`, `excludedSegments`, `segmentExclude`.
 * Responses may still include older keys; `extractAndPreprocessData` maps them into form state.
 */

/**
 * Wire payload: `includedSegmentGroups` only (no flat `segments`).
 * Grouped UI passes through; a flat id list is wrapped as a single group (uses `buildIncludedSegmentGroups`).
 */
const buildActivitySegmentsPayload = (formData) => {
  const segs = formData?.selectedSegments ?? formData?.segments;
  if (!Array.isArray(segs)) return {};
  const isGrouped = segs.some(Array.isArray);
  const selectedForGroups = isGrouped
    ? segs
    : (() => {
        const flat = normalizeActivitySegmentInput(segs);
        return flat.length > 0 ? [flat] : [];
      })();
  if (selectedForGroups.length === 0) return {};
  const includedSegmentGroups = buildIncludedSegmentGroups(
    {selectedSegments: selectedForGroups},
    []
  );
  return includedSegmentGroups.length > 0 ? {includedSegmentGroups} : {};
};

/**
 * Map form `excludedSegments` chips (`{ label, value }` or string ids) to API `{ segmentId, segmentName }[]`.
 * Optional segment catalog supplies names; otherwise uses chip `label` / `segmentName` or id.
 */
const buildActivityExcludedSegmentsForApi = (
  formData,
  segmentsCatalog = []
) => {
  if (!Array.isArray(formData?.excludedSegments)) return [];
  const segmentsLookup = new Map(
    (segmentsCatalog || []).map((s) => [
      String(s.id ?? s.value),
      s.name || s.label
    ])
  );
  return formData.excludedSegments.map((s) => {
    const segmentId = String(s?.value ?? s?.id ?? s ?? '');
    const fromCatalog = segmentsLookup.get(segmentId);
    let segmentName = fromCatalog;
    if (segmentName == null || segmentName === '') {
      if (s != null && typeof s === 'object' && !Array.isArray(s)) {
        segmentName = s.label ?? s.segmentName;
      }
    }
    if (segmentName == null || segmentName === '') {
      segmentName = segmentId;
    }
    return {segmentId, segmentName: String(segmentName)};
  });
};

/** Wire payload: `segmentExclude` and optional `excludedSegments`. */
const buildActivityExcludedPayload = (formData, segmentsCatalog = []) => {
  const out = {};
  const excludeOn = resolveRawSegmentExcludeFromForm(formData);
  if (excludeOn !== undefined && excludeOn !== null) {
    out.segmentExclude = !!excludeOn;
  }
  if (
    Array.isArray(formData?.excludedSegments) &&
    formData.excludedSegments.length > 0
  ) {
    const wired = buildActivityExcludedSegmentsForApi(
      formData,
      segmentsCatalog
    );
    if (wired.length > 0) {
      out.excludedSegments = wired;
    }
  }
  return out;
};

/** Map API excluded list to SegmentsCard / form `{ label, value }[]`. */
const mapApiExcludedSegmentsToForm = (raw) => {
  if (!Array.isArray(raw) || raw.length === 0) return undefined;
  return raw.map((s) => {
    if (typeof s === 'string') {
      return {label: s, value: s};
    }
    const segmentId = String(s?.segmentId ?? s?.value ?? s?.id ?? '');
    const label = String(s?.segmentName ?? s?.label ?? segmentId);
    return {label, value: segmentId};
  });
};

const excludedFieldsForReviewOrder = (processedData) => {
  const {excludedSegments, segmentExclude} = processedData;
  return {
    ...(Array.isArray(excludedSegments) && excludedSegments.length > 0
      ? {excludedSegments}
      : {}),
    ...(segmentExclude !== undefined ? {segmentExclude} : {})
  };
};

/**
 * Helper function to determine if an activity is being edited in published/in-progress state
 * For Point Expiration: checks for numeric ID (status may be null)
 * For other activities: checks status
 */
const checkIsEditingPublished = (activityFormData) => {
  if (activityFormData?.activityType === ACTIVITY_TYPE.POINT_EXPIRATION) {
    return !!(activityFormData?.id || activityFormData?.activityId);
  }
  return (
    isPublished(activityFormData?.status) ||
    isInProgress(activityFormData?.status)
  );
};

const getDisplayActivityType = (activityType) => {
  switch (activityType) {
    case ACTIVITY_TYPES_ENUM.WELCOME:
      return ACTIVITY_TYPE.WELCOME_ACTIVITY;
    case ACTIVITY_TYPES_ENUM.LOGIN:
      return ACTIVITY_TYPE.LOGIN;
    case ACTIVITY_TYPES_ENUM.PLAY_GAME:
      return ACTIVITY_TYPE.PLAY_GAME;
    case ACTIVITY_TYPES_ENUM.WATCH_VIDEO:
    case ACTIVITY_TYPE.WATCH_VIDEO:
      return ACTIVITY_TYPE.WATCH_VIDEO;
    case ACTIVITY_TYPES_ENUM.SURVEY:
      return ACTIVITY_TYPE.SURVEY;
    case ACTIVITY_TYPES_ENUM.POINTS_TRANSFER:
      return ACTIVITY_TYPE.POINTS_TRANSFER;
    case ACTIVITY_TYPES_ENUM.POINTS_ON_ORDER_DELIVERY:
      return ACTIVITY_TYPE.POINTS_ON_ORDER_DELIVERY;
    case ACTIVITY_TYPES_ENUM.REDEEM_POINTS:
      return ACTIVITY_TYPE.REDEEM_POINTS;
    case ACTIVITY_TYPES_ENUM.GENERIC_ACTIVITY:
      return ACTIVITY_TYPE.GENERIC_ACTIVITY;
    case ACTIVITY_TYPES_ENUM.POINT_EXPIRATION:
      return ACTIVITY_TYPE.POINT_EXPIRATION;
    case ACTIVITY_TYPES_ENUM.PAY_BY_POINTS:
      return ACTIVITY_TYPE.PAY_BY_POINTS;
    case ACTIVITY_TYPES_ENUM.CURRENCY_TO_POINTS:
      return ACTIVITY_TYPE.CURRENCY_TO_POINTS;
    default:
      return activityType;
  }
};

const extractAndPreprocessData = (formDataFromApi) => {
  // Some list APIs return the payload under `data` key. Unwrap if present.
  const src = formDataFromApi?.data ?? formDataFromApi;
  let {
    activityType,
    pointsEarned,
    hasEndDate,
    activityStartDate: startDate,
    activityEndDate: endDate,
    dailyLoginLimit,
    allTimeLoginLimit,
    activityName,
    orderType,
    localCurrency,
    numberOfCases,
    noOfDays,
    receivePoints,
    status,
    uuid,
    id,
    activityId,
    allCustomers,
    displayFirst: displayFirstRaw,
    displayOrder: displayOrderRaw,
    customerType,
    url
  } = src;
  let pointsToRedeem;
  let localCurrencyValue;

  // Handle date field mapping
  startDate = src?.activeStartDate || src?.activityStartDate;
  endDate = src?.activeEndDate || src?.activityEndDate;

  // Convert numeric fields to strings
  if (pointsEarned) {
    pointsEarned = String(pointsEarned);
  }
  if (numberOfCases) {
    numberOfCases = String(numberOfCases);
  }
  if (receivePoints) {
    receivePoints = String(receivePoints);
  }
  if (noOfDays != null) {
    noOfDays = String(noOfDays);
  }
  if (allTimeLoginLimit != null && allTimeLoginLimit !== '') {
    allTimeLoginLimit = String(allTimeLoginLimit);
  }
  // API returns pointsEarned/localCurrency for Pay by Points; form uses pointsToRedeem/localCurrencyValue
  pointsToRedeem = src?.pointsToRedeem ?? src?.pointsEarned;
  localCurrencyValue = src?.localCurrencyValue ?? src?.localCurrency;
  if (pointsToRedeem != null) {
    pointsToRedeem = String(pointsToRedeem);
  }
  if (localCurrencyValue != null) {
    localCurrencyValue = String(localCurrencyValue);
  }

  // Set hasEndDate flag
  if (endDate && endDate !== null && endDate !== undefined) {
    hasEndDate = true;
  }

  // Includes: `includedSegmentGroups` when present; else legacy flat `segments` / `Segments`
  const rawIncluded = src?.includedSegmentGroups;
  const rawSegments = src?.selectedSegments ?? src?.segments ?? src?.Segments;

  let normalizedSegments;
  if (Array.isArray(rawIncluded) && rawIncluded.length > 0) {
    normalizedSegments = mapIncludedSegmentGroups(rawIncluded);
  } else if (Array.isArray(rawSegments) && rawSegments.length > 0) {
    const flatIds = rawSegments
      .map((s) =>
        typeof s === 'string' ? s : s?.id || s?.segmentName || s?.value
      )
      .filter(Boolean);
    normalizedSegments = flatIds.length > 0 ? [flatIds] : undefined;
  } else {
    normalizedSegments = undefined;
  }

  const segmentExclude = resolveSegmentExcludeFromApi(src);

  const rawExcluded = src?.excludedSegments;
  const excludedSegmentsForm = mapApiExcludedSegmentsToForm(rawExcluded);

  const displayFirstSrc =
    displayFirstRaw ?? src?.display_first ?? src?.displayFirst;
  const displayFirst =
    displayFirstSrc === undefined || displayFirstSrc === null
      ? false
      : Boolean(displayFirstSrc);

  const displayOrderFromApi =
    displayOrderRaw ?? src?.display_order ?? src?.displayOrder;
  const hasDisplayOrderFromApi =
    displayOrderFromApi !== undefined &&
    displayOrderFromApi !== null &&
    displayOrderFromApi !== '';
  const displayOrder = resolveDisplayOrderFromApi({
    hasDisplayOrderFromApi,
    displayOrderFromApi,
    displayFirst
  });

  return {
    activityType,
    pointsEarned,
    hasEndDate,
    startDate,
    endDate,
    dailyLoginLimit,
    allTimeLoginLimit,
    activityName,
    orderType,
    localCurrency,
    numberOfCases,
    noOfDays,
    receivePoints,
    status,
    uuid,
    id,
    activityId,
    selectedSegments: normalizedSegments,
    allCustomers,
    ...(segmentExclude !== undefined ? {segmentExclude} : {}),
    ...(excludedSegmentsForm ? {excludedSegments: excludedSegmentsForm} : {}),
    pointsToRedeem,
    localCurrencyValue,
    displayFirst,
    displayOrder,
    customerType: Array.isArray(customerType) ? customerType : [],
    url: url ?? ''
  };
};

const processDates = (startDate, endDate) => {
  const processedStartDate = getDateFromISOString(startDate);
  const processedEndDate = endDate ? getDateFromISOString(endDate) : endDate;
  return {processedStartDate, processedEndDate};
};

const createWelcomeActivityOrder = (processedData) => {
  const {
    activityType,
    pointsEarned,
    hasEndDate,
    processedStartDate: startDate,
    activityName,
    uuid,
    status,
    activityId,
    processedEndDate: endDate,
    selectedSegments,
    allCustomers
  } = processedData;

  return {
    activityType: activityType,
    pointsEarned: pointsEarned,
    hasEndDate: hasEndDate,
    startDate: startDate,
    ...(activityName && {activityName: activityName}),
    ...(uuid && {uuid: uuid}),
    ...(status && {status: status}),
    ...(activityId && {id: activityId}),
    ...(hasEndDate && endDate != null && endDate !== '' && {endDate: endDate}),
    ...(Array.isArray(selectedSegments) &&
      selectedSegments.length > 0 && {selectedSegments}),
    ...(allCustomers && {allCustomers}),
    ...excludedFieldsForReviewOrder(processedData)
  };
};

const createVideoActivityOrder = (processedData) => {
  const {
    activityType,
    pointsEarned,
    processedStartDate: startDate,
    processedEndDate: endDate,
    activityName,
    uuid,
    status,
    activityId,
    selectedSegments,
    allCustomers,
    allTimeLoginLimit
  } = processedData;

  return {
    activityType,
    pointsEarned,
    startDate,
    endDate,
    ...(activityName && {activityName}),
    ...(uuid && {uuid}),
    ...(status && {status}),
    ...(activityId && {id: activityId}),
    ...(allTimeLoginLimit != null &&
      allTimeLoginLimit !== '' && {
        allTimeLoginLimit: String(allTimeLoginLimit)
      }),
    ...(Array.isArray(selectedSegments) &&
      selectedSegments.length > 0 && {selectedSegments}),
    ...(allCustomers !== undefined && {allCustomers}),
    ...excludedFieldsForReviewOrder(processedData)
  };
};

const createSurveyActivityOrder = (processedData) => {
  const {
    activityType,
    pointsEarned,
    processedStartDate: startDate,
    processedEndDate: endDate,
    activityName,
    uuid,
    status,
    activityId,
    selectedSegments,
    allCustomers,
    displayFirst,
    displayOrder,
    customerType,
    url
  } = processedData;

  return {
    activityType,
    pointsEarned,
    startDate,
    endDate,
    displayFirst: displayFirst === true,
    ...(activityName && {activityName}),
    ...(uuid && {uuid}),
    ...(status && {status}),
    ...(activityId && {id: activityId}),
    ...(Array.isArray(selectedSegments) &&
      selectedSegments.length > 0 && {selectedSegments}),
    ...(allCustomers !== undefined && {allCustomers}),
    ...(Array.isArray(customerType) && customerType.length > 0
      ? {customerType}
      : {}),
    ...(url && {url}),
    ...excludedFieldsForReviewOrder(processedData),
    ...(!displayFirst &&
      displayOrder !== undefined &&
      displayOrder !== null &&
      displayOrder !== '' && {displayOrder})
  };
};

const createLoginActivityOrder = (processedData) => {
  const {
    activityType,
    pointsEarned,
    dailyLoginLimit,
    allTimeLoginLimit,
    processedStartDate: startDate,
    processedEndDate: endDate,
    receivePoints,
    activityName,
    uuid,
    status,
    activityId,
    selectedSegments,
    allCustomers
  } = processedData;

  return {
    activityType: activityType,
    pointsEarned: pointsEarned,
    ...(dailyLoginLimit && {dailyLoginLimit: dailyLoginLimit}),
    ...(allTimeLoginLimit && {allTimeLoginLimit: String(allTimeLoginLimit)}),
    startDate: startDate,
    endDate: endDate,
    receivePoints: receivePoints,
    ...(activityName && {activityName: activityName}),
    ...(uuid && {uuid: uuid}),
    ...(status && {status: status}),
    ...(activityId && {id: activityId}),
    ...(Array.isArray(selectedSegments) &&
      selectedSegments.length > 0 && {selectedSegments}),
    ...(allCustomers && {allCustomers}),
    ...excludedFieldsForReviewOrder(processedData)
  };
};

const createPointsOnOrderDeliveryActivityOrder = (
  processedData,
  countryCode
) => {
  const {
    activityType,
    activityName,
    orderType,
    uuid,
    status,
    activityId,
    processedStartDate: startDate,
    processedEndDate: endDate,
    numberOfCases,
    pointsEarned,
    localCurrency,
    selectedSegments,
    allCustomers
  } = processedData;

  const isCountryCodeFrequency = COUNTRY_CODES_FREQUENCY.includes(countryCode);

  const fieldsArranged = {
    activityType: activityType,
    activityName: activityName,
    orderType: orderType,
    uuid: uuid,
    status: status,
    id: activityId,
    startDate: startDate,
    endDate: endDate,
    hasEndDate: true,
    ...(Array.isArray(selectedSegments) &&
      selectedSegments.length > 0 && {selectedSegments}),
    ...(allCustomers && {allCustomers}),
    ...excludedFieldsForReviewOrder(processedData)
  };

  // Add frequency-based fields
  if (isCountryCodeFrequency) {
    fieldsArranged.numberOfCases = numberOfCases;
    fieldsArranged.pointsEarned = pointsEarned;
  } else {
    fieldsArranged.localCurrency = localCurrency;
    fieldsArranged.pointsEarned = pointsEarned;
  }

  // Filter out any undefined or null values
  return Object.entries(fieldsArranged).reduce((acc, [key, value]) => {
    if (value !== undefined && value !== null) {
      acc[key] = value;
    }
    return acc;
  }, {});
};

// Create Point Expiration activity order
const createPointExpirationActivityOrder = (processedData) => {
  const {
    activityType,
    noOfDays,
    uuid,
    status,
    id,
    activityId,
    selectedSegments,
    allCustomers
  } = processedData;

  // Use id if available, otherwise use activityId
  const numericId = id || activityId;

  return {
    activityType: activityType,
    noOfDays: noOfDays,
    ...(uuid && {uuid: uuid}),
    ...(status && {status: status}),
    ...(numericId && {id: numericId}),
    ...(Array.isArray(selectedSegments) &&
      selectedSegments.length > 0 && {selectedSegments}),
    ...(allCustomers && {allCustomers}),
    ...excludedFieldsForReviewOrder(processedData)
  };
};

const createPayByPointsActivityOrder = (processedData) => {
  const {
    activityType,
    activityName,
    pointsToRedeem,
    localCurrencyValue,
    uuid,
    status,
    activityId,
    processedStartDate: startDate,
    processedEndDate: endDate,
    hasEndDate,
    selectedSegments,
    allCustomers
  } = processedData;

  const fieldsArranged = {
    activityType: activityType,
    activityName: activityName,
    pointsToRedeem: pointsToRedeem,
    localCurrencyValue: localCurrencyValue,
    uuid: uuid,
    status: status,
    id: activityId,
    startDate: startDate,
    endDate: endDate,
    ...(hasEndDate != null && {hasEndDate}),
    ...(Array.isArray(selectedSegments) &&
      selectedSegments.length > 0 && {selectedSegments}),
    ...(allCustomers && {allCustomers}),
    ...excludedFieldsForReviewOrder(processedData)
  };

  return Object.entries(fieldsArranged).reduce((acc, [key, value]) => {
    if (value !== undefined && value !== null) {
      acc[key] = value;
    }
    return acc;
  }, {});
};

const createCurrencyToPointsActivityOrder = (processedData) => {
  const {
    activityType,
    activityName,
    localCurrency,
    pointsEarned,
    uuid,
    status,
    activityId,
    processedStartDate: startDate,
    processedEndDate: endDate,
    hasEndDate,
    selectedSegments,
    allCustomers
  } = processedData;

  // Extract numeric value from "10 sar" format
  const localCurrencyValue = localCurrency?.split(' ')[0] || localCurrency;

  const fieldsArranged = {
    activityType: activityType,
    ...(activityName && {activityName: activityName}),
    localCurrency: localCurrencyValue,
    pointsEarned: pointsEarned,
    uuid: uuid,
    status: status,
    id: activityId,
    startDate: startDate,
    ...(hasEndDate != null && {hasEndDate}),
    endDate: endDate,
    ...(Array.isArray(selectedSegments) &&
      selectedSegments.length > 0 && {selectedSegments}),
    ...(allCustomers && {allCustomers}),
    ...excludedFieldsForReviewOrder(processedData)
  };

  return Object.entries(fieldsArranged).reduce((acc, [key, value]) => {
    if (value !== undefined && value !== null) {
      acc[key] = value;
    }
    return acc;
  }, {});
};

export default function useActivityReview() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [isCancel, setIsCancel] = useState(false);
  const activityFormData = useSelector((state) => state.activity.formData);
  const [isNameValid, setIsNameValid] = useState(true);

  const {
    showToast,
    toastMessage,
    handleActivityDurationValidation,
    handleToastClose,
    resetValidation,
    handleViewActivity,
    handlePointExpirationPublishError
  } = useActivityValidation();

  const getActivityEnum = (activityType) => {
    if (activityType === ACTIVITY_TYPE.WELCOME_ACTIVITY) {
      return ACTIVITY_TYPES_ENUM.WELCOME;
    }
    if (activityType === ACTIVITY_TYPE.LOGIN) {
      return ACTIVITY_TYPES_ENUM.LOGIN;
    }
    if (activityType === ACTIVITY_TYPE.PLAY_GAME) {
      return ACTIVITY_TYPES_ENUM.PLAY_GAME;
    }
    if (isVideoActivityType(activityType)) {
      return ACTIVITY_TYPES_ENUM.WATCH_VIDEO;
    }
    if (activityType === ACTIVITY_TYPE.SURVEY) {
      return ACTIVITY_TYPES_ENUM.SURVEY;
    }
    if (activityType === ACTIVITY_TYPE.POINTS_TRANSFER) {
      return ACTIVITY_TYPES_ENUM.POINTS_TRANSFER;
    }
    if (activityType === ACTIVITY_TYPE.POINTS_ON_ORDER_DELIVERY) {
      return ACTIVITY_TYPES_ENUM.POINTS_ON_ORDER_DELIVERY;
    }
    if (activityType === ACTIVITY_TYPE.REDEEM_POINTS) {
      return ACTIVITY_TYPES_ENUM.REDEEM_POINTS;
    }
    if (activityType === ACTIVITY_TYPE.GENERIC_ACTIVITY) {
      return ACTIVITY_TYPES_ENUM.GENERIC_ACTIVITY;
    }
    if (activityType === ACTIVITY_TYPE.POINT_EXPIRATION) {
      return ACTIVITY_TYPES_ENUM.POINT_EXPIRATION;
    }
    if (activityType === ACTIVITY_TYPE.PAY_BY_POINTS) {
      return ACTIVITY_TYPES_ENUM.PAY_BY_POINTS;
    }
    if (activityType === ACTIVITY_TYPE.SURVEY) {
      return ACTIVITY_TYPES_ENUM.SURVEY;
    }
    if (activityType === ACTIVITY_TYPE.CURRENCY_TO_POINTS) {
      return ACTIVITY_TYPES_ENUM.CURRENCY_TO_POINTS;
    }
    return null;
  };

  const getSlug = (activityType) => {
    // Point Expiration should use fixed slug for mobile app compatibility
    if (activityType === ACTIVITY_TYPE.POINT_EXPIRATION) {
      return 'reward-points::rolling-points-expiry';
    }
    if (activityType === ACTIVITY_TYPE.PAY_BY_POINTS) {
      return `reward-points::pay-by-points-${Date.now()}`;
    }
    return `reward-points::${activityType.replace(/\s+/g, '-').toLowerCase()}-${Date.now()}`;
  };

  const handleResetFormData = () => {
    const defaultFormData = {
      activityType: '',
      hasEndDate: false
    };
    dispatch(resetActivityData(defaultFormData));
  };

  const countryCode = useAppSelector((state) => state.countryCode).countryCode;

  const getActivityName = () => {
    const {activityType, uuid, activityName} = activityFormData || {};

    // For Points on Order Delivery, always use the activity name from form data
    if (activityType === ACTIVITY_TYPE.POINTS_ON_ORDER_DELIVERY) {
      return activityName;
    }

    if (activityType === ACTIVITY_TYPE.SURVEY) {
      return activityName && String(activityName).trim()
        ? String(activityName).trim()
        : `SURVEY_${formatUTCTimestamp()}`;
    }

    if (isVideoActivityType(activityType)) {
      return activityName && String(activityName).trim()
        ? String(activityName).trim()
        : `WATCH_VIDEO_${formatUTCTimestamp()}`;
    }

    // For Pay by Points: use form activityName or generate PAY_BY_POINTS_YYYYMMDD_HHMMSS (backend format)
    if (activityType === ACTIVITY_TYPE.PAY_BY_POINTS) {
      return activityName || `PAY_BY_POINTS_${formatUTCTimestamp()}`;
    }

    // For Currency to Points: use form activityName or generate CURRENCY_TO_POINTS_YYYYMMDD_HHMMSS
    if (activityType === ACTIVITY_TYPE.CURRENCY_TO_POINTS) {
      return activityName || `CURRENCY_TO_POINTS_${formatUTCTimestamp()}`;
    }

    // Generate timestamp-based name for:
    // 1. New activities without UUID
    // 2. Welcome/Login activities without a custom name
    const shouldUseTimestampName =
      !uuid || (typeOfActivity.includes(activityType) && !activityName);

    if (shouldUseTimestampName) {
      return `${getActivityEnum(activityType)}_${formatUTCTimestamp()}`;
    }

    // Use custom activity name if available
    return activityName;
  };

  const validateName = async (name) => {
    const isPointsOnOrderOrPayByPoints =
      activityFormData?.activityType ===
        ACTIVITY_TYPE.POINTS_ON_ORDER_DELIVERY ||
      activityFormData?.activityType === ACTIVITY_TYPE.PAY_BY_POINTS;
    const isSurvey = activityFormData?.activityType === ACTIVITY_TYPE.SURVEY;
    const isVideo = isVideoActivityType(activityFormData?.activityType);
    if (isPointsOnOrderOrPayByPoints || isSurvey || isVideo) {
      if (!name || String(name).trim() === '') {
        dispatch(
          setToastNotifications({
            show: true,
            message: isSurvey
              ? 'Survey Name is mandatory'
              : isVideo
                ? 'Video Name is mandatory'
                : 'Activity Name is mandatory',
            type: 'error'
          })
        );
        return false;
      }
      const id = activityFormData?.id ?? null;
      const uuid = activityFormData?.uuid ?? null;
      const params = {
        id,
        uuid,
        name
      };
      const resp = await handleNameValidationViaCallbackFn(
        validateActivityName,
        params
      );
      const isValid = resp?.success === true;
      setIsNameValid(isValid);
      if (!isValid) {
        dispatch(
          setToastNotifications({
            show: true,
            message:
              "It looks like there's already an Activity with this name. Please enter a unique name before continuing.",
            type: 'error'
          })
        );
      }
      return isValid;
    } else {
      setIsNameValid(true);
      return true;
    }
  };

  const isPointExpirationDuplicateError = (errorMessage) => {
    if (activityFormData?.activityType !== ACTIVITY_TYPE.POINT_EXPIRATION) {
      return false;
    }
    const message = (errorMessage || '').toLowerCase();
    return (
      message.includes('points expiry') || message.includes('already exists')
    );
  };

  const fetchExistingPointExpirationActivity = async () => {
    const response = await handleGetActivity(
      `activityType=${ACTIVITY_TYPES_ENUM.POINT_EXPIRATION}`,
      false
    );
    return (
      response?.data?.content?.[0]?.activityId ||
      response?.data?.content?.[0]?.id
    );
  };

  const handlePointExpirationDuplicateError = async () => {
    try {
      const existingActivityId = await fetchExistingPointExpirationActivity();
      handlePointExpirationPublishError(existingActivityId);
    } catch {
      handlePointExpirationPublishError(null);
    }
  };

  const prepareDates = () => {
    const activityStartDateIso = getISOTimeStringForReview(
      activityFormData?.startDate,
      false,
      true
    );
    const activityEndDateIso = getISOTimeStringForReview(
      activityFormData?.endDate,
      true,
      false
    );
    const shouldSendEndDate =
      activityFormData?.activityType === ACTIVITY_TYPE.WELCOME_ACTIVITY ||
      activityFormData?.activityType === ACTIVITY_TYPE.PAY_BY_POINTS
        ? !!activityFormData?.hasEndDate
        : true;

    return {
      activityStartDateValue: activityStartDateIso ?? '',
      activityEndDateValue: shouldSendEndDate ? (activityEndDateIso ?? '') : ''
    };
  };

  const validatePublishDuration = async (
    uniqueActivityName,
    isEditingPublished
  ) => {
    let validationParams = {
      ...activityFormData,
      activityName: uniqueActivityName
    };
    if (isEditingPublished) {
      validationParams.id =
        activityFormData?.id || activityFormData?.activityId;
    }
    return await handleActivityDurationValidation(
      omitDeprecatedSegmentWireKeys(validationParams)
    );
  };

  const buildPublishParams = (
    uniqueActivityName,
    dates,
    isEditingPublished
  ) => {
    const isPayByPoints =
      activityFormData?.activityType === ACTIVITY_TYPE.PAY_BY_POINTS;
    const isCurrencyToPoints =
      activityFormData?.activityType === ACTIVITY_TYPE.CURRENCY_TO_POINTS;

    // Format local currency with currency code for Currency to Points
    const formattedLocalCurrency =
      isCurrencyToPoints && activityFormData?.localCurrency
        ? `${activityFormData.localCurrency} ${getCurrencySymbol(countryCode).toLowerCase()}`
        : isPayByPoints
          ? activityFormData?.localCurrencyValue
          : activityFormData?.localCurrency;

    return {
      title: '',
      activityType: getActivityEnum(activityFormData?.activityType),
      tags: isPayByPoints
        ? 'paid-by-points'
        : isCurrencyToPoints
          ? 'currency-to-points'
          : '',
      slug: getSlug(activityFormData?.activityType),
      pointsEarned: isPayByPoints
        ? activityFormData?.pointsToRedeem
        : activityFormData?.pointsEarned,
      maxLimitPerUser: 0,
      dailyLoginLimit: activityFormData?.dailyLoginLimit,
      conversionRate: 0,
      amountPerUnit: 0,
      pointsAwardedPerUnit: 0,
      activityStartDate: dates.activityStartDateValue,
      activityEndDate: dates.activityEndDateValue,
      allTimeLoginLimit: activityFormData?.allTimeLoginLimit,
      activityName: uniqueActivityName,
      ...(!isEditingPublished && {
        uuid: activityFormData?.uuid || getUUID(uniqueActivityName)
      }),
      orderType: activityFormData?.orderType,
      localCurrency: formattedLocalCurrency,
      ...(COUNTRY_CODES_FREQUENCY.includes(countryCode)
        ? {numberOfCases: activityFormData?.numberOfCases}
        : {}),
      hasEndDate: activityFormData?.hasEndDate,
      receivePoints: activityFormData?.receivePoints,
      noOfDays: activityFormData?.noOfDays,
      ...(activityFormData?.activityType === ACTIVITY_TYPE.SURVEY
        ? {
            displayFirst: !!activityFormData?.displayFirst,
            ...(Array.isArray(activityFormData?.customerType) &&
            activityFormData.customerType.length > 0
              ? {customerType: activityFormData.customerType}
              : {}),
            ...(activityFormData?.url ? {url: activityFormData.url} : {}),
            ...(!activityFormData?.displayFirst &&
            activityFormData?.displayOrder !== undefined &&
            activityFormData?.displayOrder !== null &&
            activityFormData?.displayOrder !== ''
              ? {displayOrder: activityFormData.displayOrder}
              : {})
          }
        : {}),
      ...(isVideoActivityType(activityFormData?.activityType)
        ? {
            allTimeLoginLimit: activityFormData?.allTimeLoginLimit
          }
        : {}),
      status: ACTIVITY_STATUS.PUBLISHED,
      ...(isEditingPublished && {activityName: activityFormData.activityName}),
      ...(isEditingPublished && {
        id: activityFormData.id || activityFormData.activityId
      }),
      allCustomers: activityFormData?.allCustomers,
      ...buildActivitySegmentsPayload(activityFormData),
      ...buildActivityExcludedPayload(activityFormData)
    };
  };

  const filterPublishParams = (param, dates) => {
    const manuallyIncludedKeys = [
      'tags',
      'applicationId',
      'slug',
      'activityName',
      'uuid',
      'status',
      'id',
      'includedSegmentGroups',
      'allCustomers',
      'segmentExclude',
      'excludedSegments',
      'pointsEarned',
      'localCurrency',
      'hasEndDate',
      'activityStartDate',
      'activityEndDate',
      'displayFirst',
      'displayOrder'
    ];

    const isOptionalEndDateActivity =
      activityFormData?.activityType === ACTIVITY_TYPE.WELCOME_ACTIVITY ||
      activityFormData?.activityType === ACTIVITY_TYPE.PAY_BY_POINTS;

    return Object.fromEntries(
      Object.entries(param).filter(([key, value]) => {
        if (
          key === 'activityEndDate' &&
          isOptionalEndDateActivity &&
          !activityFormData?.hasEndDate
        ) {
          return false;
        }

        if (manuallyIncludedKeys.includes(key)) {
          return true;
        }

        const isFromFormData =
          key in activityFormData ||
          value === getActivityEnum(activityFormData?.activityType) ||
          value === getSlug(activityFormData?.activityType) ||
          value === dates.activityStartDateValue ||
          value === dates.activityEndDateValue ||
          value === activityFormData?.orderType;

        return (
          isFromFormData &&
          value !== undefined &&
          value !== null &&
          value !== ''
        );
      })
    );
  };

  const handlePublishSuccess = (isEditingPublished) => {
    dispatch(setActivityFormData(activityFormData));
    dispatch(
      setToastNotifications({
        show: true,
        message: isEditingPublished
          ? 'Activity updated successfully'
          : 'New Activity published',
        type: 'success'
      })
    );
    resetValidation();
    handleResetFormData();
    navigate(ROUTES.ACTIVITIES);
  };

  const handlePublishError = async (error, isResponse = false) => {
    const errorMessage = isResponse
      ? error?.message
      : error?.cause || error?.message || '';

    if (isPointExpirationDuplicateError(errorMessage)) {
      if (!isResponse) {
        dispatch(
          setToastNotifications({show: false, message: '', type: 'error'})
        );
      }
      await handlePointExpirationDuplicateError();
      return;
    }

    const errMsg = isResponse
      ? error?.errorMessage || error?.message || 'An unknown error occurred'
      : error?.cause ||
        error?.response?.data?.errorMessage ||
        error?.response?.data?.message ||
        error?.message ||
        'An unknown error occurred';

    dispatch(
      setToastNotifications({show: true, message: errMsg, type: 'error'})
    );
  };

  const handlePublish = async (options = {}) => {
    const {onSuccess} = options;
    if (!activityFormData?.activityType) {
      dispatch(
        setToastNotifications({
          show: true,
          message: 'Activity Type is mandatory',
          type: 'error'
        })
      );
      return;
    }

    const uniqueActivityName = getActivityName();
    const isCurrencyToPoints =
      activityFormData?.activityType === ACTIVITY_TYPE.CURRENCY_TO_POINTS;

    // Skip name validation for Currency to Points (auto-generated name)
    if (activityFormData?.status == null && !isCurrencyToPoints) {
      const isValidName = await validateName(uniqueActivityName);
      if (!isValidName) return;
    }

    const dates = prepareDates();
    const isEditingPublished = checkIsEditingPublished(activityFormData);

    // Skip duration validation for Currency to Points (API doesn't support it yet)
    if (!isCurrencyToPoints) {
      const isDurationValid = await validatePublishDuration(
        uniqueActivityName,
        isEditingPublished
      );
      if (!isDurationValid) return;
    }

    const param = buildPublishParams(
      uniqueActivityName,
      dates,
      isEditingPublished
    );
    const formBasedParams = omitDeprecatedSegmentWireKeys(
      filterPublishParams(param, dates)
    );

    try {
      const response = isEditingPublished
        ? await handleUpdateActivity({data: formBasedParams})
        : await handleCreateActivity({data: formBasedParams});

      if (response?.success) {
        if (onSuccess) {
          dispatch(
            setActivityFormData({...activityFormData, status: 'PUBLISHED'})
          );
          onSuccess(response);
        } else {
          handlePublishSuccess(isEditingPublished);
        }
      } else {
        await handlePublishError(response, true);
      }
    } catch (error) {
      await handlePublishError(error, false);
    }
  };

  const buildBaseDraftData = (uniqueActivityName, dates) => {
    const draft = {
      ...activityFormData,
      activityName: uniqueActivityName,
      activityStartDate: dates.activityStartDateValue,
      activityEndDate: dates.activityEndDateValue,
      activityType: getActivityEnum(activityFormData?.activityType),
      status: ACTIVITY_STATUS.DRAFT,
      uuid: activityFormData?.uuid || getUUID(uniqueActivityName),
      ...buildActivitySegmentsPayload(activityFormData),
      ...buildActivityExcludedPayload(activityFormData)
    };
    return omitDeprecatedSegmentWireKeys(draft);
  };

  const transformPayByPointsData = (draftData) => {
    draftData.tags = 'paid-by-points';
    draftData.slug = getSlug(activityFormData?.activityType);
    draftData.pointsEarned = activityFormData?.pointsToRedeem;
    draftData.localCurrency = activityFormData?.localCurrencyValue;
    delete draftData.pointsToRedeem;
    delete draftData.localCurrencyValue;
  };

  const saveDraft = async (draftData) => {
    return activityFormData?.uuid
      ? await handleUpdateDraftActivity({data: draftData})
      : await handleSaveDraftActivity({data: draftData});
  };

  const handleDraftResponse = (response) => {
    let message = 'Failed to save draft';
    if (response?.success) {
      if (activityFormData?.uuid) {
        message = 'Activity draft updated';
      } else {
        message = 'New Activity draft';
      }
    }
    const type = response?.success ? 'success' : 'error';

    dispatch(setToastNotifications({show: true, message, type}));

    if (response?.success) {
      handleResetFormData();
      navigate(ROUTES.ACTIVITIES);
    }
  };

  const handleDraft = async () => {
    if (!activityFormData?.activityType) {
      dispatch(
        setToastNotifications({
          show: true,
          message: 'Activity Type is mandatory',
          type: 'error'
        })
      );
      return;
    }

    const uniqueActivityName = getActivityName();
    const isValidName = await validateName(uniqueActivityName);
    if (!isValidName) return;

    const dates = prepareDates();
    const baseDraftData = buildBaseDraftData(uniqueActivityName, dates);

    const isPayByPointsDraft =
      activityFormData?.activityType === ACTIVITY_TYPE.PAY_BY_POINTS;

    if (isPayByPointsDraft) {
      transformPayByPointsData(baseDraftData);
    }

    const response = await saveDraft(baseDraftData);
    handleDraftResponse(response);
  };

  const handleCancel = () => {
    setIsCancel((v) => !v);
  };

  const onClickPrimaryButton = () => {
    handleResetFormData();
    navigate(ROUTES.ACTIVITIES);
  };

  const onClickSecondaryButton = () => {
    setIsCancel((v) => !v);
  };

  const handleValidationModalClose = () => {
    setIsNameValid(true);
  };

  const getFormDataWithOrder = useCallback(
    (formDataFromApi) => {
      // Extract and preprocess data
      const extractedData = extractAndPreprocessData(formDataFromApi);

      // Process dates
      const {processedStartDate, processedEndDate} = processDates(
        extractedData.startDate,
        extractedData.endDate
      );

      // Get display activity type
      const displayActivityType = getDisplayActivityType(
        extractedData.activityType
      );

      // Combine all processed data
      const processedData = {
        ...extractedData,
        activityType: displayActivityType,
        processedStartDate,
        processedEndDate
      };

      // Return appropriate order based on activity type
      if (displayActivityType === ACTIVITY_TYPE.WELCOME_ACTIVITY) {
        return createWelcomeActivityOrder(processedData);
      } else if (displayActivityType === ACTIVITY_TYPE.LOGIN) {
        return createLoginActivityOrder(processedData);
      } else if (
        displayActivityType === ACTIVITY_TYPE.POINTS_ON_ORDER_DELIVERY
      ) {
        return createPointsOnOrderDeliveryActivityOrder(
          processedData,
          countryCode
        );
      } else if (displayActivityType === ACTIVITY_TYPE.POINT_EXPIRATION) {
        return createPointExpirationActivityOrder(processedData);
      } else if (displayActivityType === ACTIVITY_TYPE.PAY_BY_POINTS) {
        return createPayByPointsActivityOrder(processedData);
      } else if (displayActivityType === ACTIVITY_TYPE.SURVEY) {
        return createSurveyActivityOrder(processedData);
      } else if (isVideoActivityType(displayActivityType)) {
        return createVideoActivityOrder(processedData);
      } else if (displayActivityType === ACTIVITY_TYPE.CURRENCY_TO_POINTS) {
        return createCurrencyToPointsActivityOrder(processedData);
      } else {
        return {};
      }
    },
    [countryCode]
  );

  return {
    handlePublish,
    handleDraft,
    handleCancel,
    onClickPrimaryButton,
    onClickSecondaryButton,
    isCancel,
    activityFormData,
    getFormDataWithOrder,
    isNameValid,
    handleValidationModalClose,
    validateName,
    getActivityName,
    showToast,
    toastMessage,
    handleToastClose,
    handleViewActivity,
    handleActivityDurationValidation,
    resetValidation
  };
}
